/**
 * The dates of a stay, pure (batch-29, item R1; plan §3.3 and §5.2). An event has one of three date modes:
 *   fixed — everyone stays the event's dates; nothing to pick
 *   extra — the event's dates, plus up to N nights before and N nights after (one number for both)
 *   open  — any nights inside the event's own dates, between a shortest and a longest stay
 * Days are `YYYY-MM-DD`. A stay from `arrive` to `leave` sleeps the nights arrive … leave − 1.
 */
import { addDays } from '../../lib/weeks';
import { daysBetween } from './businessDays';

export type StayMode = 'fixed' | 'extra' | 'open';
export interface StayRules {
  mode: StayMode;
  /** the event's first day */
  start: string;
  /** the event's last day (a one-day event has start = end and is one night) */
  end: string;
  /** extra: nights allowed before and after, 0..14 */
  extraNights?: number | null;
  /** open: shortest stay in nights; null = 1 */
  minNights?: number | null;
  /** open: longest stay in nights; null = the whole window */
  maxNights?: number | null;
}
export type StayProblem = 'dates_invalid' | 'stay_too_short' | 'stay_too_long' | 'night_not_sold';
/** Whether a night can be booked. The page builds it from the room rates; see `nightSold`. */
export type Sold = (night: string) => boolean;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** The day the event's own stay ends: its last day, or the day after its first when it is a one-day event. */
export const eventLeave = (r: Pick<StayRules, 'start' | 'end'>): string => (r.end > r.start ? r.end : addDays(r.start, 1));
/** The stay everyone gets on a fixed-date event, and the stay the other modes start from. */
export const eventStay = (r: Pick<StayRules, 'start' | 'end'>): { arrive: string; leave: string } => ({ arrive: r.start, leave: eventLeave(r) });

export const nightCount = (arrive: string, leave: string): number => daysBetween(arrive, leave);
/** The nights a stay sleeps, in order. */
export function stayNights(arrive: string, leave: string): string[] {
  const n = nightCount(arrive, leave);
  return n > 0 ? Array.from({ length: n }, (_, i) => addDays(arrive, i)) : [];
}

/**
 * Whether a night is sold. Inside the event's dates: the price typed for that night, else the every-night price.
 * Outside them: only a price typed for that night. A price of 0, or nothing typed, is not sold.
 */
export function nightSold(night: string, r: Pick<StayRules, 'start' | 'end'>, typed: Record<string, number | null | undefined>, everyNightCents: number | null | undefined): boolean {
  const own = typed[night];
  const inside = night >= r.start && night < eventLeave(r);
  const cents = own ?? (inside ? everyNightCents : null);
  return typeof cents === 'number' && cents > 0;
}

const extra = (r: StayRules): number => Math.min(14, Math.max(0, Math.trunc(r.extraNights ?? 0)));
/** The earliest arrival and the latest departure the mode allows. */
export function stayWindow(r: StayRules): { first: string; last: string } {
  const leave = eventLeave(r);
  if (r.mode === 'extra') return { first: addDays(r.start, -extra(r)), last: addDays(leave, extra(r)) };
  return { first: r.start, last: leave };
}

/** Why these dates cannot be booked, or null when they can. */
export function stayProblem(r: StayRules, arrive: string, leave: string, sold: Sold = () => true): StayProblem | null {
  if (!DAY.test(arrive) || !DAY.test(leave) || !(arrive < leave)) return 'dates_invalid';
  if (addDays(arrive, 0) !== arrive || addDays(leave, 0) !== leave) return 'dates_invalid';
  const ev = eventStay(r);
  const w = stayWindow(r);
  const n = nightCount(arrive, leave);
  if (!Number.isFinite(n) || n < 1) return 'dates_invalid';
  if (r.mode === 'fixed') {
    if (arrive !== ev.arrive || leave !== ev.leave) return 'dates_invalid';
  } else if (r.mode === 'extra') {
    // the event's own nights are always part of the stay; only the nights around them are the guest's choice
    if (arrive < w.first || arrive > ev.arrive || leave < ev.leave || leave > w.last) return 'dates_invalid';
  } else {
    if (arrive < w.first || leave > w.last) return 'dates_invalid';
    if (n < Math.max(1, r.minNights ?? 1)) return 'stay_too_short';
    if (r.maxNights != null && n > r.maxNights) return 'stay_too_long';
  }
  return stayNights(arrive, leave).every(sold) ? null : 'night_not_sold';
}

/** The arrival days to offer: each one starts at least one bookable stay. Empty on a fixed-date event (nothing to pick). */
export function arriveChoices(r: StayRules, sold: Sold = () => true): string[] {
  if (r.mode === 'fixed') return [];
  const w = stayWindow(r);
  const days = stayNights(w.first, w.last);
  return days.filter((d) => leaveChoices(r, d, sold).length > 0);
}

/** The departure days to offer for an arrival day. */
export function leaveChoices(r: StayRules, arrive: string, sold: Sold = () => true): string[] {
  if (r.mode === 'fixed') return [];
  const w = stayWindow(r);
  const out: string[] = [];
  for (let d = addDays(arrive, 1); d <= w.last; d = addDays(d, 1)) if (stayProblem(r, arrive, d, sold) === null) out.push(d);
  return out;
}
